/**
 * `usePublishLayoutConfig` — the hook a migrated configurable component calls to
 * publish its **base** (pre-override) declarative config to the
 * {@link layoutConfigStore}, so the admin layout editor can build a complete,
 * reliable target list (title / actions / placeholders / hidden fields / info
 * items) instead of scraping the rendered preview DOM.
 *
 * Same philosophy as `setRuntimeProps` (written by `withComponentRules` on
 * every render): publishing is a cheap, unconditional store write. To avoid
 * spamming the store on every render with a freshly-built config object, the
 * hook writes inside a `useEffect` keyed on a **stable signature** of the
 * config, so it only re-publishes when something an editor target depends on
 * actually changed. Only the admin config page reads the store, but publishing
 * unconditionally keeps the call site a single line.
 *
 * A migrated component calls this at the SAME site that calls
 * `applyLayoutOverrides` — i.e. with the **base** config in hand — typically
 * via one of the `buildPublishedConfigFrom*` builders below:
 *
 * ```ts
 * usePublishLayoutConfig(
 *   COMPONENT_ID,
 *   buildPublishedConfigFromEntityForm(baseConfig, 'Ajouter un intervenant'),
 * );
 * const config = useMemo(
 *   () => applyLayoutOverrides(baseConfig, _layoutOverrides),
 *   [baseConfig, _layoutOverrides],
 * );
 * ```
 *
 * See `.claude-work/plans/layout-editor-free-text-editing.md` §2.2.
 */

import { useEffect } from 'react';

import type {
  ActionsConfig,
  EntityFormConfig,
  FieldConfig,
  SectionConfig,
} from '@components/integrated/entity-form';
import type {
  ColumnConfig,
  DataListConfig,
} from '@components/integrated/data-list';

import { setLayoutConfig } from './layoutConfigStore';
import type { PublishedLayoutConfig } from './layoutConfigStore';
import type { InfoItemDescriptor } from './layoutOverrides';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Publish a component's base {@link PublishedLayoutConfig} to the
 * {@link layoutConfigStore} under `componentId`.
 *
 * No-op when `cfg` is `undefined` (e.g. a component that has nothing to
 * publish yet, or a render before its config resolves). When `cfg` is present
 * the effect re-runs only when the config's stable signature changes, so a
 * component that rebuilds its config object every render (the common case)
 * does not thrash the store.
 *
 * @param componentId - The component id (matches `ComponentRegistration.id`).
 * @param cfg         - The base (pre-override) config, or `undefined`.
 */
export function usePublishLayoutConfig(
  componentId: string,
  cfg?: PublishedLayoutConfig,
): void {
  // Stable signature: the editor only cares about hostKind, the title, and the
  // identity + count of each field / column / section / action. We serialise a
  // compact projection so a brand-new-but-equal config object does not re-fire
  // the effect (mirrors the dedupe `handleTargetsDiscovered` does downstream).
  const signature = cfg === undefined ? '' : signatureOf(componentId, cfg);

  useEffect(() => {
    if (cfg === undefined) return;
    setLayoutConfig(componentId, cfg);
    // The signature stands in for `cfg`: when it is unchanged the published
    // config is layout-equivalent, so re-writing it is pointless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, signature]);
}

/**
 * Compact, deterministic signature of the parts of a published config the
 * editor's targets depend on. Used as the `useEffect` dependency so a
 * structurally-equal config rebuilt each render does not re-publish.
 */
function signatureOf(componentId: string, cfg: PublishedLayoutConfig): string {
  const parts: string[] = [componentId, cfg.hostKind, cfg.title ?? ''];
  if (cfg.fields !== undefined) {
    for (const [key, f] of Object.entries(cfg.fields)) {
      parts.push(
        `f:${key}:${f.type}:${f.label}:${f.placeholder ?? ''}:${
          f.helperText ?? ''
        }:${f.tooltip ?? ''}:${String(f.visible)}:${String(f.order ?? '')}`,
      );
    }
  }
  if (cfg.sections !== undefined) {
    for (const s of cfg.sections) {
      parts.push(`s:${s.id}:${s.title ?? ''}:${String(s.order ?? '')}`);
    }
  }
  if (cfg.actions !== undefined) {
    for (const key of ['submit', 'cancel', 'reset'] as const) {
      const a = cfg.actions[key];
      if (a !== undefined) {
        parts.push(`a:${key}:${a.label ?? ''}:${String(a.visible)}`);
      }
    }
    for (const c of cfg.actions.custom ?? []) {
      parts.push(`a:${c.id}:${c.label ?? ''}:${String(c.visible)}`);
    }
  }
  if (cfg.columns !== undefined) {
    for (const [key, c] of Object.entries(cfg.columns)) {
      parts.push(
        `c:${key}:${c.label}:${c.headerTooltip ?? ''}:${String(
          c.hidden,
        )}:${String(c.order ?? '')}`,
      );
    }
  }
  if (cfg.items !== undefined) {
    for (const item of cfg.items) {
      parts.push(
        `i:${item.key}:${item.label ?? ''}:${item.tooltip ?? ''}:${String(
          item.visible,
        )}:${String(item.order ?? '')}`,
      );
    }
  }
  if (cfg.filters !== undefined) {
    for (const f of cfg.filters) {
      parts.push(
        `flt:${f.id}:${f.label}:${f.placeholder ?? ''}:${String(
          f.hidden,
        )}:${String(f.order ?? '')}`,
      );
    }
  }
  if (cfg.search !== undefined) {
    parts.push(
      `srch:${String(cfg.search.enabled)}:${cfg.search.placeholder ?? ''}`,
    );
  }
  return parts.join('|');
}

// ---------------------------------------------------------------------------
// Convenience builders
// ---------------------------------------------------------------------------

/**
 * Build a {@link PublishedLayoutConfig} from an EntityForm base config.
 *
 * Copies `fields` (always), and `sections` / `actions` when present. `title`
 * is the resolved base title — pass the value the card renders today
 * (typically its `Card.Header` title or `config.i18n?.title`); it falls back to
 * `config.i18n?.title` when omitted.
 *
 * @param cfg   - The base {@link EntityFormConfig} (pre-override).
 * @param title - The resolved base title, if the card renders one.
 */
export function buildPublishedConfigFromEntityForm(
  cfg: EntityFormConfig,
  title?: string,
): PublishedLayoutConfig {
  const fields: Record<string, FieldConfig> = cfg.fields;
  const sections: SectionConfig[] | undefined = cfg.sections;
  const actions: ActionsConfig | undefined = cfg.actions;
  const resolvedTitle = title ?? cfg.i18n?.title;
  return {
    hostKind: 'entity-form',
    fields,
    ...(sections !== undefined ? { sections } : {}),
    ...(actions !== undefined ? { actions } : {}),
    ...(resolvedTitle !== undefined ? { title: resolvedTitle } : {}),
  };
}

/**
 * Build a {@link PublishedLayoutConfig} from a DataList base config.
 *
 * Copies `columns` (always). `title` is the resolved base title — pass the
 * value the card renders today; it falls back to `config.i18n?.title` then
 * `config.display?.title` when omitted (the order `DataListHeader` resolves
 * them). DataList has no action buttons, so `actions` is never published.
 *
 * @param cfg   - The base {@link DataListConfig} (pre-override).
 * @param title - The resolved base title, if the card renders one.
 */
export function buildPublishedConfigFromDataList(
  cfg: DataListConfig,
  title?: string,
): PublishedLayoutConfig {
  const columns: Record<string, ColumnConfig> = cfg.columns;
  const resolvedTitle = title ?? cfg.i18n?.title ?? cfg.display?.title;
  const filters = cfg.toolbar?.filters;
  const search = cfg.toolbar?.search;
  return {
    hostKind: 'data-list',
    columns,
    ...(filters !== undefined && filters.length > 0 ? { filters } : {}),
    ...(search !== undefined
      ? { search: { enabled: search.enabled, placeholder: search.placeholder } }
      : {}),
    ...(resolvedTitle !== undefined ? { title: resolvedTitle } : {}),
  };
}

/**
 * Build a {@link PublishedLayoutConfig} from an array of InfoList item
 * descriptors (the override-able surface — `key` / `label` / `order` /
 * `visible` / `tooltip`). The consuming card owns the title (InfoList has no
 * config title), so pass it explicitly when the card renders one.
 *
 * @param items - The base info-card descriptors, in declaration order.
 * @param title - The card's title, if it renders one.
 */
export function buildPublishedConfigFromInfoItems(
  items: InfoItemDescriptor[],
  title?: string,
): PublishedLayoutConfig {
  return {
    hostKind: 'info-list',
    items,
    ...(title !== undefined ? { title } : {}),
  };
}
